import { Link } from 'react-router-dom'
import { SeverityDonut } from '@/shared/components/SeverityDonut'
import { HelpIcon } from '@/shared/components/HelpIcon'
import type { TenantAnalytics } from '@/lib/apiTypes'
import { formatCount } from '@/lib/format'
import { rowStyle } from '@/features/dashboard/cards/styles'

/**
 * Continuous-patching exposure: superseded published versions with open critical/high
 * findings that consumers are STILL pulling. "Open findings" covers HEAD; this covers the
 * tail being deployed anyway - the number a continuous-patch org drives to zero. Renders
 * nothing on an older worker (absent field), and an explicit all-clear when measured clean,
 * since a silently missing exposure card would read as "no data", not "no exposure".
 */
export function PatchAdoptionCard({ adoption }: { adoption: TenantAnalytics['patch_adoption'] }) {
  if (!adoption) return null
  const maxPulls = adoption.worst[0]?.downloads_30d ?? 1
  return (
    <div className="card fu" style={{ marginBottom: 20 }}>
      <div className="card-header">
        <div>
          <div className="kicker">Security</div>
          <h3 style={{ fontSize: '0.95rem' }}>Patch adoption</h3>
        </div>
        <HelpIcon track="help_patch_adoption" title="Patch adoption" kicker="// what this implies">
          <p>Downloads, in the last 30 days, of module versions that are
          <strong> superseded</strong> and carry open <strong>critical / high</strong>
          findings. These consumers are deploying risk your registry has already moved past.</p>
          <p>Chase this to zero: nudge consumers onto the latest version (or loosen their
          pins), <strong>deprecate</strong> the affected versions, or <strong>block</strong>
          them so a known-bad artefact can't keep being pulled. A target marked "also
          affected" means publishing a clean version comes first.</p>
        </HelpIcon>
      </div>
      {adoption.worst.length === 0 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '18px' }}>
          <span style={{ fontFamily: 'var(--head)', fontSize: '1.2rem', color: 'var(--green)' }}>✓</span>
          <span style={{ fontFamily: 'var(--mono)', fontSize: '0.72rem', color: 'var(--text3)' }}>
            No superseded versions with open critical or high findings were downloaded in the last 30 days.
          </span>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
            <span style={{ fontFamily: 'var(--head)', fontWeight: 700, fontSize: '1.3rem', color: 'var(--amber)' }}>
              {formatCount(adoption.vulnerable_downloads_30d)}
            </span>
            <span style={{ fontFamily: 'var(--mono)', fontSize: '0.65rem', color: 'var(--text3)' }}>
              vulnerable pulls (30d) · {formatCount(adoption.vulnerable_versions)} superseded version{adoption.vulnerable_versions === 1 ? '' : 's'} with open critical/high findings
            </span>
          </div>
          {adoption.worst.map((v, i) => (
            <Link
              key={i}
              to={`/org/${v.namespace}/modules/${v.name}/${v.provider}/${v.version}`}
              style={rowStyle}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg3)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = '')}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: '0.8rem', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {v.name}/{v.provider} <span className="dim">v{v.version}</span>
                  </span>
                  <span style={{ flex: 1 }} />
                  <SeverityDonut
                    critical={v.critical_count}
                    high={v.high_count}
                    total={v.critical_count + v.high_count}
                    focusable={false}
                  />
                  <span style={{ fontFamily: 'var(--head)', fontSize: '0.9rem', color: 'var(--amber)', flexShrink: 0 }}>
                    {formatCount(v.downloads_30d)} pulls
                  </span>
                </div>
                <div aria-hidden style={{ height: 4, background: 'var(--bg3)', borderRadius: 2, margin: '7px 0 5px' }}>
                  <div style={{ width: `${Math.max((v.downloads_30d / maxPulls) * 100, 2)}%`, height: '100%', background: 'var(--amber)', borderRadius: 2 }} />
                </div>
                <div style={{ fontSize: '0.68rem', color: 'var(--text3)' }}>
                  {v.latest_version === null
                    ? 'no newer published version'
                    : v.latest_open_findings === 0
                      ? <>upgrade target v{v.latest_version} <span style={{ color: 'var(--green)' }}>clean</span></>
                      : <>latest v{v.latest_version} <span style={{ color: 'var(--amber)' }}>also affected</span></>}
                </div>
              </div>
            </Link>
          ))}
        </>
      )}
    </div>
  )
}

